// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useEffect, useRef, useState } from "react"
import type { QueueItem } from "@/lib/review/queue"
import type { Glossary } from "@/lib/review/glossary"
import { type LocateState } from "./locate-state"
import type { ReviewPlace } from "./jump-to-string"
import type { Tier } from "@/scripts/review-ledger"
import { TIER_LABEL } from "./tier-labels"
import { PlacesPanel } from "./places-panel"
import { useReviewItemActions } from "./use-review-item-actions"
import { UndoToast } from "./undo-toast"
import {
  swipeAction,
  isHorizontalSwipe,
} from "@/components/editor/swipe-action"
import {
  useSignoffError,
  retrySignoff,
  clearSignoffError,
} from "./pending-commits"
import type { Overview } from "@/lib/review/overview"
import { PrStatusBoard } from "./pr-status-board"

interface MobileReviewViewProps {
  idToken: string
  glossary: Glossary
  queue: QueueItem[]
  current: QueueItem | null
  cursor: number
  locate: LocateState
  places: ReviewPlace[]
  onPreview: (href: string) => void
  /** Tier+search-filtered queue (with original indices) for the drawer list. */
  filtered: { item: QueueItem; i: number }[]
  availableTiers: Tier[]
  tierSel: Set<Tier>
  onToggleTier: (t: Tier) => void
  /** Count of strings the active tier filter hides; 0 = nothing hidden. */
  hiddenByTier: number
  onShowAllTiers: () => void
  locales: string[]
  locale: string | null
  onSetLocale: (l: string) => void
  onSkip: () => void
  onUndo: () => void
  onOpen: (queueIndex: number) => void
  loading: boolean
  /** Team-wide review state for the backlog-aware empty-state. Null while loading. */
  overview: Overview | null
}

const LOCATE_NOTICE: Record<Exclude<LocateState, "page">, string> = {
  chrome: "Shared UI — shown across the site, not tied to one page.",
  template: "Contains placeholders ({…}) — can't highlight in context.",
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

/** Don't start a swipe from inside an editable / actionable control — the
 *  textarea needs the pointer to place the cursor and select text. */
function isInteractiveTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null
  return !!el?.closest(
    "textarea, button, input, select, a, summary, [contenteditable]"
  )
}

/**
 * The single review card. Split out so its `key` (key:locale) remounts it per
 * item — resetting the draft via useReviewItemActions, mirroring the desktop
 * ReviewPanel's keyed remount.
 */
function ReviewCard({
  item,
  idToken,
  glossary,
  cursor,
  total,
  locate,
  places,
  onPreview,
  onSkip,
}: {
  item: QueueItem
  idToken: string
  glossary: Glossary
  cursor: number
  total: number
  locate: LocateState
  places: ReviewPlace[]
  onPreview: (href: string) => void
  onSkip: () => void
}) {
  const {
    value,
    setValue,
    busy,
    edited,
    issues,
    blocked,
    approveAsIs,
    editAndApprove,
  } = useReviewItemActions({ item, onDone: onSkip, idToken, glossary })
  const targetRef = useRef<HTMLTextAreaElement>(null)
  const [dx, setDx] = useState(0)
  // Pulse the issues region when a blocked approve is attempted, so the swipe
  // doesn't just snap back silently with no explanation.
  const [flashBlocked, setFlashBlocked] = useState(false)
  const errorCount = issues.filter((i) => i.severity === "error").length
  // Pointer origin; `axis` latches once the gesture commits to a direction so a
  // mid-drag wobble can't flip a scroll into a swipe.
  const dragStart = useRef<{ x: number; y: number } | null>(null)
  const axis = useRef<"horizontal" | "vertical" | null>(null)

  function approve() {
    if (busy) return
    if (blocked) {
      // Don't silently swallow a blocked approve — pulse the error region so the
      // reviewer sees why the card snapped back.
      setFlashBlocked(true)
      window.setTimeout(() => setFlashBlocked(false), 1200)
      return
    }
    if (edited) editAndApprove()
    else approveAsIs()
  }

  function focusEdit() {
    targetRef.current?.focus()
  }

  const reduced = prefersReducedMotion()

  function onPointerDown(e: React.PointerEvent) {
    if (reduced) return
    // Let the textarea / buttons own their own pointer interactions.
    if (isInteractiveTarget(e.target)) return
    dragStart.current = { x: e.clientX, y: e.clientY }
    axis.current = null
  }
  function onPointerMove(e: React.PointerEvent) {
    if (dragStart.current === null) return
    const ddx = e.clientX - dragStart.current.x
    const ddy = e.clientY - dragStart.current.y
    // Dominant-axis lock: decide once we've moved enough to read intent.
    if (axis.current === null && Math.abs(ddx) + Math.abs(ddy) > 8) {
      axis.current = isHorizontalSwipe(ddx, ddy) ? "horizontal" : "vertical"
      // It's a scroll — bow out so the body scrolls normally.
      if (axis.current === "vertical") {
        dragStart.current = null
        setDx(0)
        return
      }
      e.currentTarget.setPointerCapture(e.pointerId)
    }
    if (axis.current === "horizontal") setDx(ddx)
  }
  function onPointerUp() {
    if (dragStart.current === null) return
    const action = swipeAction(dx)
    if (action === "approve") approve()
    else if (action === "skip") onSkip()
    // Snap back to rest; the CSS transition animates sub-threshold release.
    setDx(0)
    dragStart.current = null
    axis.current = null
  }

  const action = swipeAction(dx)
  const hintOpacity = Math.min(1, Math.abs(dx) / 96)

  return (
    <div className="ahead-mobile-review-card-wrap">
      {/* Resting affordance: faint always-on chevrons so first-timers know the
          card swipes. Fades out once an active drag hint takes over. */}
      <div
        className="ahead-mobile-review-rest"
        aria-hidden="true"
        style={action !== "none" ? { opacity: 0 } : undefined}
      >
        <span className="ahead-mobile-review-rest-skip">‹ skip</span>
        <span className="ahead-mobile-review-rest-approve">approve ›</span>
      </div>
      {!reduced && action !== "none" ? (
        <div
          className={`ahead-mobile-review-hint ${
            action === "approve" ? "approve" : "skip"
          }`}
          style={{ opacity: hintOpacity }}
          aria-hidden="true"
        >
          {action === "approve" ? "Approve" : "Skip"}
        </div>
      ) : null}
      <div
        className="ahead-mobile-review-card"
        style={reduced ? undefined : { transform: `translateX(${dx}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <div className="ahead-mobile-review-progress" aria-live="polite">
          {cursor + 1} / {total}
        </div>
        <div className="ahead-mobile-review-meta">
          {item.key} · {item.tier} · {item.status}
        </div>
        {locate !== "page" ? (
          <>
            <p className="ahead-mobile-review-banner" role="note">
              {LOCATE_NOTICE[locate]}
            </p>
            <PlacesPanel places={places} onPreview={onPreview} />
          </>
        ) : null}
        <label className="ahead-mobile-review-field">
          EN source
          <textarea readOnly value={item.enValue} aria-label="EN source" />
        </label>
        <label className="ahead-mobile-review-field">
          {item.locale}
          <textarea
            ref={targetRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={(e) => e.currentTarget.scrollIntoView({ block: "center" })}
            aria-label={item.locale}
          />
        </label>
      </div>
      {issues.length > 0 ? (
        <div
          className={[
            "ahead-mobile-review-issues-wrap",
            flashBlocked ? "is-flashing" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {blocked ? (
            <p className="ahead-mobile-review-issues-blocker" role="alert">
              {errorCount === 1
                ? "1 error blocks approval"
                : `${errorCount} errors block approval`}
            </p>
          ) : null}
          <ul className="ahead-mobile-review-issues" role="note">
            {issues.map((issue) => (
              <li
                key={`${issue.code}:${issue.severity}`}
                data-severity={issue.severity}
              >
                {issue.severity === "error" ? "Error" : "Warning"} —{" "}
                {issue.message}
              </li>
            ))}
          </ul>
          {blocked ? (
            <p className="ahead-mobile-review-blocked-hint" role="note">
              Fix the error above to approve.
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="ahead-mobile-review-actions">
        <button
          type="button"
          className="ahead-mobile-review-skip"
          disabled={busy}
          onClick={onSkip}
        >
          Skip
        </button>
        <button
          type="button"
          className="ahead-mobile-review-edit"
          onClick={focusEdit}
        >
          Edit
        </button>
        <button
          type="button"
          className="ahead-mobile-review-approve"
          disabled={busy || blocked}
          aria-busy={busy}
          onClick={approve}
        >
          {busy ? (
            <span className="ahead-mobile-review-spinner" aria-hidden="true" />
          ) : null}
          Approve
        </button>
      </div>
    </div>
  )
}

/**
 * Mobile presentation for the translation review queue. Receives already-computed
 * state + handlers from ReviewWorklistBody (the single data/logic owner) — no
 * fetching here. Renders a single review card with a thumb-zone action bar and
 * swipe-to-act, a locale chip row, a collapsible queue list, and the shared undo
 * snackbar. The same useReviewItemActions hook the desktop panel uses drives
 * sign-off, so mobile and desktop write the identical cart + pending-commit path.
 */
export function MobileReviewView({
  idToken,
  glossary,
  queue,
  current,
  cursor,
  locate,
  places,
  onPreview,
  filtered,
  availableTiers,
  tierSel,
  onToggleTier,
  hiddenByTier,
  onShowAllTiers,
  locales,
  locale,
  onSetLocale,
  onSkip,
  onUndo,
  onOpen,
  loading,
  overview,
}: MobileReviewViewProps) {
  const [listOpen, setListOpen] = useState(false)
  const signoffError = useSignoffError()

  // Keep the jumped/highlighted string above the bottom sheet: findAndHighlight's
  // scrollIntoView honours scroll-padding, so reserve the lower viewport while the
  // mobile review view is mounted. Desktop never mounts this component, so its
  // scroll behavior is untouched. (AHD-7504)
  useEffect(() => {
    const root = document.documentElement
    root.style.scrollPaddingBottom = "60vh"
    return () => {
      root.style.scrollPaddingBottom = ""
    }
  }, [])

  return (
    <div className="ahead-mobile-review" data-ahead-editor-ui>
      <UndoToast onUndo={onUndo} />

      {signoffError ? (
        <div className="ahead-mobile-review-error" role="alert">
          <strong>Couldn’t save “{signoffError.key}”.</strong>
          <div className="ahead-mobile-review-error-actions">
            <button
              type="button"
              className="ahead-mobile-review-error-retry"
              onClick={retrySignoff}
            >
              Retry
            </button>
            <button
              type="button"
              className="ahead-mobile-review-error-dismiss"
              aria-label="Dismiss"
              onClick={clearSignoffError}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}

      {locales.length > 1 ? (
        <div
          className="ahead-mobile-review-locales"
          role="group"
          aria-label="Review locale"
        >
          {locales.map((l) => (
            <button
              key={l}
              type="button"
              className="ahead-mobile-review-locale"
              aria-pressed={l === locale}
              onClick={() => onSetLocale(l)}
            >
              {l}
            </button>
          ))}
        </div>
      ) : null}

      {availableTiers.length > 1 ? (
        <div
          className="ahead-mobile-review-tiers"
          role="group"
          aria-label="Filter by tier"
        >
          {availableTiers.map((t) => (
            <button
              key={t}
              type="button"
              className="ahead-mobile-review-tier"
              data-tier={t}
              aria-pressed={tierSel.has(t)}
              onClick={() => onToggleTier(t)}
            >
              {TIER_LABEL[t]}
            </button>
          ))}
        </div>
      ) : null}

      {hiddenByTier > 0 ? (
        <div className="ahead-mobile-review-tierhint" role="note">
          {hiddenByTier} more {hiddenByTier === 1 ? "string is" : "strings are"}{" "}
          hidden by the tier filter.{" "}
          <button
            type="button"
            className="ahead-mobile-review-tierhint-clear"
            onClick={onShowAllTiers}
          >
            Show all tiers
          </button>
        </div>
      ) : null}

      {loading ? (
        <div
          className="ahead-mobile-review-loading"
          role="status"
          aria-live="polite"
        >
          Loading strings…
        </div>
      ) : null}

      {current ? (
        <ReviewCard
          key={`${current.key}:${current.locale}`}
          item={current}
          idToken={idToken}
          glossary={glossary}
          cursor={cursor}
          total={queue.length}
          locate={locate}
          places={places}
          onPreview={onPreview}
          onSkip={onSkip}
        />
      ) : !loading ? (
        <div className="ahead-mobile-review-empty" role="status">
          {locales.length === 0 ? (
            overview && overview.backlog.total > 0 ? (
              <>
                <p>
                  You have no review scope assigned, but this PR is still
                  blocked on{" "}
                  {overview.localeStatus
                    .filter((r) => r.count > 0)
                    .map((r) => r.locale)
                    .join(", ")}
                  .
                </p>
                <PrStatusBoard
                  rows={overview.localeStatus}
                  reviewLink={
                    typeof window === "undefined" ? "" : window.location.href
                  }
                />
              </>
            ) : (
              <p>
                You have no review scope assigned yet. Ask an admin to add you
                to the <code>translation_reviewer</code> table.
              </p>
            )
          ) : overview && overview.backlog.total > 0 ? (
            <>
              <p>
                ✅ Your locales are signed off. This PR is still blocked on{" "}
                {overview.localeStatus
                  .filter((r) => r.count > 0)
                  .map((r) => r.locale)
                  .join(", ")}
                .
              </p>
              <PrStatusBoard
                rows={overview.localeStatus}
                reviewLink={
                  typeof window === "undefined" ? "" : window.location.href
                }
              />
            </>
          ) : overview ? (
            <p>🎉 All locales signed off — this PR is green.</p>
          ) : (
            <p>Nothing left to review — you&apos;re all caught up.</p>
          )}
        </div>
      ) : null}

      {filtered.length > 0 ? (
        <div className="ahead-mobile-review-queue">
          <button
            type="button"
            className="ahead-mobile-review-queue-toggle"
            aria-expanded={listOpen}
            onClick={() => setListOpen((o) => !o)}
          >
            Queue (
            {filtered.length !== queue.length
              ? `${filtered.length} of ${queue.length}`
              : queue.length}
            ) {listOpen ? "▾" : "▸"}
          </button>
          {listOpen ? (
            <ul className="ahead-mobile-review-queue-list">
              {filtered.map(({ item: q, i }) => (
                <li key={`${q.key}:${q.locale}`}>
                  <button
                    type="button"
                    className="ahead-mobile-review-queue-item"
                    aria-current={q === current}
                    onClick={() => onOpen(i)}
                  >
                    <code>{q.key}</code> <small>{q.status}</small>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
